<template>
  <div class="flex flex-col h-full overflow-hidden relative">
    <!-- Preview Mode -->
    <template v-if="previewForm">
      <!-- Header -->
      <div class="flex-none flex items-center justify-between py-4 border-b bg-white">
        <div class="flex items-center gap-3">
<!--          <button-->
<!--            class="p-2 -mr-2 hover:bg-surface-100 rounded-full text-surface-600 transition-colors"-->
<!--            @click="previewForm = null"-->
<!--          >-->
<!--            <i class="pi pi-arrow-right text-xl"></i>-->
<!--          </button>-->
          <h1 class="font-bold text-lg text-surface-800 truncate max-w-[250px] md:max-w-[400px]">
            {{ previewForm.title }}
          </h1>
        </div>
        <div class="flex items-center gap-2 text-xs text-primary-600 font-medium bg-primary-50 px-3 py-1.5 rounded-full">
          <i class="pi pi-sparkles text-sm"></i>
          <span class="whitespace-nowrap">נוצר עם AI</span>
        </div>
      </div>

      <!-- Preview Form Fields -->
      <div class="flex-1 overflow-y-auto bg-surface-50">
        <div class="px-4 py-6 space-y-6 max-w-2xl mx-auto">
          <div v-for="el in previewForm.elements" :key="el.id">
            <!-- Short Text -->
            <div v-if="el.type === 'short_text'">
              <label class="block text-sm font-bold text-surface-800 mb-1.5">
                {{ el.label }}
                <span v-if="el.required" class="text-red-500">*</span>
              </label>
              <InputText
                :placeholder="el.helperText"
                class="w-full"
                disabled
              />
            </div>

            <!-- Checkboxes -->
            <div v-else-if="el.type === 'checkboxes'" class="space-y-3">
              <label class="block text-sm font-bold text-surface-800 mb-1.5">
                {{ el.label }}
                <span v-if="el.required" class="text-red-500">*</span>
              </label>
              <label
                v-for="(opt, i) in el.options"
                :key="i"
                class="flex items-center gap-3 p-4 bg-white border border-surface-200 rounded-2xl shadow-sm opacity-70"
              >
                <Checkbox :binary="true" disabled />
                <span class="text-sm font-medium text-surface-700 flex-1">{{ opt }}</span>
              </label>
            </div>

            <!-- Multiple Choice -->
            <div v-else-if="el.type === 'multiple_choice'" class="space-y-3">
              <label class="block text-sm font-bold text-surface-800 mb-1.5">
                {{ el.label }}
                <span v-if="el.required" class="text-red-500">*</span>
              </label>
              <label
                v-for="(opt, i) in el.options"
                :key="i"
                class="flex items-center gap-3 p-4 bg-white border border-surface-200 rounded-2xl shadow-sm opacity-70"
              >
                <RadioButton :value="opt" :name="el.id" disabled />
                <span class="text-sm font-medium text-surface-700">{{ opt }}</span>
              </label>
            </div>

            <!-- Read Only -->
            <div v-else-if="el.type === 'read_only'" class="mb-4">
              <label class="block text-sm font-bold text-surface-800 mb-2">{{ el.label }}</label>
              <div class="bg-surface-100/50 p-5 rounded-2xl border border-surface-200 text-sm text-surface-600 leading-relaxed whitespace-pre-wrap">
                {{ el.content }}
              </div>
            </div>

            <!-- Signature -->
            <div v-else-if="el.type === 'signature'">
              <label class="block text-sm font-bold text-surface-800 mb-1.5">
                {{ el.label }}
                <span v-if="el.required" class="text-red-500">*</span>
              </label>
              <div class="bg-surface-100 border-2 border-dashed border-surface-300 rounded-2xl p-8 text-center text-surface-400">
                <i class="pi pi-pen-to-square text-3xl mb-2 block"></i>
                <span class="text-sm">שדה חתימה</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Save Button -->
      <div class="flex-none w-full py-4 bg-white border-t border-surface-100 shadow-[0_-4px_20px_rgba(0,0,0,0.02)]">
        <Button
          label="שמירה ועריכה"
          icon="pi pi-pencil"
          class="w-full !py-3 !text-lg !font-bold"
          @click="handleSavePreview"
        />
      </div>
    </template>

    <!-- Creation Mode -->
    <template v-else>
      <div class="flex-1 overflow-y-auto pb-4 space-y-5 bg-white">
        <!-- From Scratch -->
        <button
          class="w-full flex items-center gap-4 p-4 rounded-2xl hover:bg-surface-50 transition-colors text-right border border-surface-100 hover:border-primary-200 group"
          :disabled="generating"
          @click="handleFromScratch"
        >
          <div class="bg-surface-50 text-surface-500 group-hover:bg-primary-100 group-hover:text-primary-600 p-3 rounded-xl transition-colors">
            <i class="pi pi-file-edit text-2xl"></i>
          </div>
          <div>
            <div class="font-semibold text-surface-800">מאפס</div>
            <div class="text-xs text-surface-500 mt-0.5">יצירת טופס ריק לעיצוב עצמי</div>
          </div>
        </button>

        <!-- AI Generation Section -->
        <div class="pt-2">
          <div class="flex items-center gap-2 text-xs font-bold text-primary-600 tracking-wider uppercase mb-3">
            <i class="pi pi-sparkles text-sm"></i>
            <span>יצירה עם AI</span>
          </div>

          <div class="bg-white rounded-2xl shadow-sm border border-surface-100 p-4 space-y-4">
            <!-- Description Textarea -->
            <div>
              <label class="block text-sm font-medium text-surface-700 mb-2">תארו את הטופס שתרצו ליצור</label>
              <Textarea
                v-model="description"
                :disabled="generating"
                rows="4"
                class="w-full"
                placeholder="לדוגמה: טופס הסכמה לטיפולי בוטוקס שאוסף מידע על אלרגיות, תרופות, היריון, מצבים רפואיים ומכיל הצהרת הסכמה וחתימה"
              />
            </div>

            <!-- Quick Template Suggestions -->
            <div>
              <div class="text-xs text-surface-500 mb-2">או בחרו תבנית מוכנה:</div>
              <div class="flex flex-wrap gap-2">
                <button
                  v-for="template in templateOptions"
                  :key="template.type"
                  class="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border transition-colors"
                  :class="selectedTemplate === template.type
                    ? 'bg-primary-100 border-primary-300 text-primary-700'
                    : 'bg-surface-50 border-surface-200 text-surface-600 hover:bg-primary-50 hover:border-primary-200 hover:text-primary-600'"
                  :disabled="generating"
                  @click="selectTemplate(template)"
                >
                  <i :class="template.icon" class="text-xs"></i>
                  {{ template.label }}
                </button>
              </div>
            </div>

            <!-- Generate Button -->
            <Button
              label="יצירת טופס עם AI"
              icon="pi pi-sparkles"
              class="w-full"
              :loading="generating"
              :disabled="!description.trim()"
              @click="handleGenerate"
            />
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import type { IConsentForm } from '@torim-app/types';
import { ConsentFormElementTypeEnum } from '@torim-app/types';
import { ConsentFormsApi } from '@app/api/admin';
import { useToast } from 'vue-toastification';

const props = defineProps<{
  onCreated: (form: IConsentForm) => void;
  onClose: () => void;
}>();

const toast = useToast();
const generating = ref(false);
const description = ref('');
const selectedTemplate = ref('');
const previewForm = ref<IConsentForm | null>(null);

const templateOptions = [
  {
    type: 'general_beauty',
    icon: 'pi pi-sparkles',
    label: 'טיפולי יופי כלליים',
    description: 'טופס הסכמה כללי לטיפולי יופי הכולל הצהרת בריאות, אלרגיות, רגישויות עור, מצבים רפואיים, היריון, תרופות, הכרה בסיכונים וחתימה',
  },
  {
    type: 'injectables',
    icon: 'pi pi-heart',
    label: 'הזרקות (בוטוקס/פילר)',
    description: 'טופס הסכמה לטיפולי הזרקות אסתטיים (בוטוקס, פילרים, מזותרפיה) הכולל היסטוריה רפואית, אלרגיות, מדללי דם, מחלות אוטואימוניות, היריון/הנקה, תגובות קודמות להזרקות, הכרה בסיכונים ותוצאות צפויות וחתימה',
  },
  {
    type: 'lash_brow',
    icon: 'pi pi-eye',
    label: 'ריסים וגבות',
    description: 'טופס הסכמה לטיפולי ריסים וגבות (הרמת ריסים, הארכות, למינציה, מיקרובליידינג) הכולל רגישויות עיניים, אלרגיות לדבקים/צבעים, ניתוחי עיניים אחרונים, שימוש בעדשות מגע, מצבי עור באזור העיניים, הנחיות טיפוח לאחר הטיפול וחתימה',
  },
];

const selectTemplate = (template: typeof templateOptions[number]) => {
  if (selectedTemplate.value === template.type) {
    selectedTemplate.value = '';
    description.value = '';
  } else {
    selectedTemplate.value = template.type;
    description.value = template.description;
  }
};

const handleFromScratch = async () => {
  try {
    const newForm = await ConsentFormsApi.createConsentForm({
      title: 'טופס הסכמה חדש',
      elements: [
        {
          id: crypto.randomUUID(),
          type: ConsentFormElementTypeEnum.ShortText,
          label: 'שם מלא',
          required: true,
          helperText: 'הזינו את שמכם המלא',
        },
        {
          id: crypto.randomUUID(),
          type: ConsentFormElementTypeEnum.Signature,
          label: 'חתימת הלקוח/ה',
          required: true,
        },
      ],
    });
    props.onCreated(newForm);
  } catch (e) {
    console.error(e);
    toast.error('שגיאה ביצירת טופס');
  }
};

const handleGenerate = async () => {
  generating.value = true;
  try {
    const newForm = await ConsentFormsApi.generateFromTemplate({
      templateType: selectedTemplate.value || undefined,
      description: description.value.trim() || undefined,
    });
    previewForm.value = newForm;
    toast.success('הטופס נוצר בהצלחה עם AI!');
  } catch (e) {
    console.error(e);
    toast.error('שגיאה ביצירת טופס עם AI');
  } finally {
    generating.value = false;
  }
};

const handleSavePreview = () => {
  if (previewForm.value) {
    props.onCreated(previewForm.value);
  }
};
</script>
